import React, { useMemo, useCallback } from 'react';
import ExperienceSection from './ExperienceSection';
import AchievementsSection from './AchievementsSection';
import HonorsSection from './HonorsSection';
import NotableContributionsSection from './NotableContributionsSection';
import PublicationsSection from './PublicationsSection';
import ReferencesSection from './ReferencesSection';
import AdditionalNotesSection from './AdditionalNotesSection';
import { primaryRoleType } from '../../../constants/staffRoles';
import FreeTextListSection from './components/FreeTextListSection';
import FreeTextAreaSection from './components/FreeTextAreaSection';
import CheckboxGroupSection from './components/CheckboxGroupSection';
import FileUploadSection from './components/FileUploadSection';

const CareerTimeline = ({ 
  isEditing,
  isSaving,
  onEdit,
  onSave,
  onCancel,
  careerData = {},
  onUpdateCareerData,
  // `additionalNotes` is the same stored field as the coaching philosophy. A
  // coaching role edits it there, so the box is hidden here for them — one
  // field must never have two input boxes.
  showAdditionalNotes = true,
  disableAutoSave = false
}) => {
  const [error, setError] = React.useState(null);

  // Handle cancel - just clear error
  const handleCancelInternal = useCallback(() => {
    setError(null);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const {
    experience = [],
    achievements = [],
    honors = [],
    contributions = [],
    publications = [],
    references = [],
    additionalNotes = "",
    // New role-specific fields (safe defaults)
    notableGkTrained = [],
    gkMethodology = "",
    teamsPlayersSupported = [],
    projectsProgramsLed = "",
    staffSupervised = "",
    keyTransfersManaged = "",
    partnershipsNetworks = "",
    tournamentTravelOrg = "",
    administrativeExperience = "",
    successfulPlayersDiscovered = [],
    teamsPlayersTreated = [],
    psychologistFocusAreas = [],
    psychologistSupported = [],
    analysisTypes = [],
    reportsPresentations = [],
    programsDeveloped = "",
    talentsPromoted = "",
    academyStructuresBuilt = "",
    academyDocs = [],
  } = careerData;

  const handleUpdateExperience = (newExperience) => {
    onUpdateCareerData({ ...careerData, experience: newExperience });
  };

  const handleUpdateAchievements = (newAchievements) => {
    onUpdateCareerData({ ...careerData, achievements: newAchievements });
  };

  const handleUpdateHonors = (newHonors) => {
    onUpdateCareerData({ ...careerData, honors: newHonors });
  };

  const handleUpdateContributions = (newContributions) => {
    onUpdateCareerData({ ...careerData, contributions: newContributions });
  };

  const handleUpdatePublications = (newPublications) => {
    onUpdateCareerData({ ...careerData, publications: newPublications });
  };

  const handleUpdateReferences = (newReferences) => {
    onUpdateCareerData({ ...careerData, references: newReferences });
  };

  const handleUpdateNotes = (newNotes) => {
    onUpdateCareerData({ ...careerData, additionalNotes: newNotes });
  };

  // Role detection from staff profile may be passed via router/state later; for now derive from props if present
  // Reduce any title (existing or new) to its canonical role for branch matching
  // so e.g. 'manager' renders the same template as 'head_coach'.
  const position = useMemo(() => primaryRoleType(careerData?.position || careerData?.role || ''), [careerData]);

  const update = (field) => (val) => onUpdateCareerData({ ...careerData, [field]: val });

  return (
    <div className="px-4 md:px-14 py-6 space-y-8">
      {/* Save/Cancel Controls */}
      {isEditing && (
        <div className="bg-white p-6 border border-[#E6E6E6] rounded-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">CAREER TIMELINE</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancelInternal}
                disabled={isSaving}
                className="border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSaving}
                className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg disabled:opacity-50"
              >
                {isSaving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="bg-red-100 px-2 py-1.5 rounded-md text-red-800 hover:bg-red-200 text-sm font-medium"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Career Timeline Sections */}
      {/* Assistant Coach -> rename Notable Contribution */}
      {position === 'assistant_coach' ? (
        <NotableContributionsSection
          isEditing={isEditing}
          contributions={contributions}
          onUpdateContributions={handleUpdateContributions}
          title="NOTABLE CONTRIBUTION / PLAYER DEVELOPED"
        />
      ) : null}

      {/* Goalkeeping Coach */}
      {position === 'goalkeeper_coach' && (
        <>
          <FreeTextListSection
            isEditing={isEditing}
            label="NOTABLE GOALKEEPERS TRAINED"
            items={notableGkTrained}
            onChange={update('notableGkTrained')}
            placeholder="Add name here"
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="METHODOLOGY"
            value={gkMethodology}
            onChange={update('gkMethodology')}
            placeholder="Explain your training methods, drills, and approach to goalkeeper development Ex: 1v1 focus, distribution, reaction speed"
          />
        </>
      )}

      {/* Strength & Conditioning Coach */}
      {position === 'strength_conditioning_coach' && (
        <FreeTextListSection
          isEditing={isEditing}
          label="TEAMS/PLAYERS SUPPORTED"
          items={teamsPlayersSupported}
          onChange={update('teamsPlayersSupported')}
          placeholder="Add details here"
        />
      )}

      {/* Technical Director */}
      {position === 'technical_director' && (
        <>
          <FreeTextAreaSection
            isEditing={isEditing}
            label="PROJECTS/PROGRAMS LED"
            value={projectsProgramsLed}
            onChange={update('projectsProgramsLed')}
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="STAFF SUPERVISED"
            value={staffSupervised}
            onChange={update('staffSupervised')}
          />
        </>
      )}

      {/* Sporting Director */}
      {position === 'sporting_director' && (
        <>
          <FreeTextAreaSection
            isEditing={isEditing}
            label="KEY TRANSFERS MANAGED"
            value={keyTransfersManaged}
            onChange={update('keyTransfersManaged')}
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="PARTNERSHIPS / NETWORKS"
            value={partnershipsNetworks}
            onChange={update('partnershipsNetworks')}
          />
        </>
      )}

      {/* Team Manager */}
      {position === 'head_coach' && (
        <>
          <FreeTextAreaSection
            isEditing={isEditing}
            label="TOURNAMENT/TRAVEL ORGANIZATION"
            value={tournamentTravelOrg}
            onChange={update('tournamentTravelOrg')}
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="ADMINISTRATIVE EXPERIENCE"
            value={administrativeExperience}
            onChange={update('administrativeExperience')}
          />
        </>
      )}

      {/* Scout / Youth Scout */}
      {position === 'scout' && (
        <FreeTextListSection
          isEditing={isEditing}
          label="SUCCESSFUL PLAYERS DISCOVERED"
          items={successfulPlayersDiscovered}
          onChange={update('successfulPlayersDiscovered')}
          placeholder="Add player name and brief note"
        />
      )}

      {/* Physio / Medical */}
      {position === 'physiotherapist' && (
        <FreeTextListSection
          isEditing={isEditing}
          label="TEAMS/PLAYERS TREATED"
          items={teamsPlayersTreated}
          onChange={update('teamsPlayersTreated')}
          placeholder="Add details here"
        />
      )}

      {/* Nutritionist */}
      {position === 'nutritionist' && (
        <FreeTextListSection
          isEditing={isEditing}
          label="TEAMS/PLAYERS SUPPORTED"
          items={teamsPlayersSupported}
          onChange={update('teamsPlayersSupported')}
          placeholder="Add details here"
        />
      )}

      {/* Sports Psychologist */}
      {position === 'psychologist' && (
        <>
          <CheckboxGroupSection
            isEditing={isEditing}
            label="FOCUS AREAS"
            value={psychologistFocusAreas}
            onChange={update('psychologistFocusAreas')}
            options={[
              { value: 'mental_resilience', label: 'Mental Resilience' },
              { value: 'performance_coaching', label: 'Performance Coaching' },
              { value: 'stress_management', label: 'Stress Management' },
              { value: 'youth_development', label: 'Youth Development' },
            ]}
          />
          <FreeTextListSection
            isEditing={isEditing}
            label="PLAYERS/TEAMS SUPPORTED"
            items={psychologistSupported}
            onChange={update('psychologistSupported')}
          />
        </>
      )}

      {/* Performance Analyst */}
      {position === 'performance_analyst' && (
        <>
          <CheckboxGroupSection
            isEditing={isEditing}
            label="ANALYSIS TYPES"
            value={analysisTypes}
            onChange={update('analysisTypes')}
            options={[
              { value: 'opposition', label: 'Opposition' },
              { value: 'own_team', label: 'Own Team' },
              { value: 'individual_player', label: 'Individual Player' },
              { value: 'data_modeling', label: 'Data Modeling' },
            ]}
          />
          <FileUploadSection
            isEditing={isEditing}
            label="REPORTS / PRESENTATIONS"
            files={reportsPresentations}
            onChange={update('reportsPresentations')}
            accept=".pdf,.ppt,.pptx,.mp4,.mov,.avi"
          />
        </>
      )}

      {/* Youth Coordinator */}
      {position === 'youth_coordinator' && (
        <>
          <FreeTextAreaSection
            isEditing={isEditing}
            label="PROGRAMS DEVELOPED"
            value={programsDeveloped}
            onChange={update('programsDeveloped')}
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="TALENTS PROMOTED"
            value={talentsPromoted}
            onChange={update('talentsPromoted')}
          />
          <FreeTextAreaSection
            isEditing={isEditing}
            label="ACADEMY STRUCTURES BUILT"
            value={academyStructuresBuilt}
            onChange={update('academyStructuresBuilt')}
          />
          <FileUploadSection
            isEditing={isEditing}
            label="ACADEMY DOCUMENTATION"
            files={academyDocs}
            onChange={update('academyDocs')}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
          />
        </>
      )}
      {/* Experience Section */}
      <ExperienceSection
        isEditing={isEditing}
        experience={experience}
        onUpdateExperience={handleUpdateExperience}
      />

      {/* Additional Notes Section */}
      {showAdditionalNotes && (
        <AdditionalNotesSection
          isEditing={isEditing}
          notes={additionalNotes}
          onUpdateNotes={handleUpdateNotes}
        />
      )}

      {/* Achievements/Trophies Section */}
      <AchievementsSection
        isEditing={isEditing}
        achievements={achievements}
        onUpdateAchievements={handleUpdateAchievements}
      />

      {/* Honors & Awards Section */}
      <HonorsSection
        isEditing={isEditing}
        honors={honors}
        onUpdateHonors={handleUpdateHonors}
      />

      {/* Default Notable Contributions when not assistant coach */}
      {position !== 'assistant_coach' && (
        <NotableContributionsSection
          isEditing={isEditing}
          contributions={contributions}
          onUpdateContributions={handleUpdateContributions}
        />
      )}

      {/* References Section */}
      <ReferencesSection
        isEditing={isEditing}
        references={references}
        onUpdateReferences={handleUpdateReferences}
      />

      {/* Publications/Media Section */}
      <PublicationsSection
        isEditing={isEditing}
        publications={publications}
        onUpdatePublications={handleUpdatePublications}
      />
      
      {/* Loading Overlay */}
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Saving career timeline...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerTimeline; 